import React, { useEffect, useState, useRef, useMemo } from "react";
import { Accordion, Button, Card } from "react-bootstrap";
import "./OpalSamples.css";
import TauriLoader2 from "../../../TauriLoader2/TauriLoader2";
import TauriImage from "../../../TauriImage/TauriImage";
import OpalAddItemModal from "./OpalAddItemModal/OpalAddItemModal";
import OpalSamplesFindModal from "./OpalSamplesFindModal/OpalInventoryFindModal";
import { addRecord, deleteRecord, deleteRecordByField, getAll, getNextId, getRecordById, updateRecord } from "../../../../localDB";
import { useBaseProvider } from "../../../../BaseManager/BaseManager";
import { transformSessionInventorySamples, transformSessionSamplesData } from "../../CoverageFactoryData/CoverageFactoryData";
import appURL from '../../../../appURL.json';
import { upsertRecordsBySessionId } from "../../../../localDB";
import OpalAlertModal from "../../../OpalAlertModal/OpalAlertModal";
import { LocationMap, statusMapping, StockAllocationType, SyncStatusMap } from "../../../../visualizations/visualizations";

const OpalSamples = ({ section, id }) => {
    const [data, setData] = useState([]);
    const [loading, setIsLoading] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [checkedRows, setCheckedRows] = useState({});
    const [showItemModal, setShowItemModal] = useState(false);
    const [showInventoryItemModal, setShowInventoryItemModal] = useState(false);
    const [session, setSession] = useState(null);
    const [item, setItem] = useState([]);
    const [search, setSearch] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [invData, setInvData] = useState([]);
    const [units, setUnits] = useState([]);

    const [batchValues, setBatchValues] = useState({
        Executed: 0,
        Remarks: ""
    });

    const { isSync } = useBaseProvider();

    const getSampleStockByItemId = async (itemId, type) => {
        const stockAllocations = await getAll("StockAllocations");

        return (stockAllocations || []).find(
            s => s.ItemID === itemId && s.Type === type
        ) || null;
    };

    const handleBatchInputTextChange = async (field, value) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        setBatchValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBatchInputNumberChange = async (event, field) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        const value = event.target.value;

        if (/^\d*$/.test(value)) {
            const parsedQty = value === "" ? "" : Math.max(0, parseInt(value, 10));
            setBatchValues(prev => ({
                ...prev,
                [field]: parsedQty === "" ? 0 : parsedQty
            }));
        }
    };

    const handleBatchUpdate = async (field, value) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        const toUpdate = Object.keys(checkedRows).filter(key => checkedRows[key]);

        await Promise.all(
            toUpdate.map(async (ID) => {
                const inventory = await getRecordById("sessionItems", Number(ID));
                if (!inventory) return;

                let updatedValue = value;

                if (field === "Executed") {
                    const stockItem = await getSampleStockByItemId(inventory.ItemID, StockAllocationType.Samples);
                    if (!stockItem) return;

                    const allocated = stockItem.Allocated || 0;
                    const available = (stockItem.Stock || 0) - allocated;

                    const currentQty = inventory[field] || 0;

                    updatedValue = Math.max(0, Math.min(value, currentQty + available));
                    const stockChange = updatedValue - currentQty;

                    if (stockChange !== 0) {
                        const newAllocated = Math.max(
                            0,
                            Math.min(stockItem.Stock, allocated + stockChange)
                        );

                        await updateRecord("StockAllocations", {
                            ...stockItem,
                            Allocated: newAllocated
                        });
                    }
                }

                const updatedRow = {
                    ...inventory,
                    [field]: updatedValue,
                    SyncStatus: inventory?.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
                };

                await updateRecord("sessionItems", updatedRow);
                return
            })
        );

        fetchData();
    };

    const handleAddSelected = async (selectedItems) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        if (!selectedItems.length) return;
        const existingItemIds = new Set(data.map(i => i.ItemID));

        const StockAllocations = await getAll("StockAllocations");

        for (const item of selectedItems) {
            if (existingItemIds.has(item.ItemID)) continue;

            const stock = StockAllocations.find(a => a.ItemID === item.ItemID && a.UnitID === item.UnitID && a.Type === StockAllocationType.Samples);

            try {
                const newId = await getNextId("sessionItems");

                if (!stock) {
                    const stockAllocationnNewId = await getNextId("StockAllocations");

                    const newStockAllocation = {
                        ID: stockAllocationnNewId,
                        ItemID: item.ItemID,
                        UnitID: item.UnitID,
                        Type: StockAllocationType.Samples,
                        Stock: 0,
                        Allocated: 0
                    }

                    await addRecord('StockAllocations', newStockAllocation);
                }

                const newItem = {
                    ID: newId,
                    ItemID: item.ItemID,
                    UnitID: item.UnitID,
                    Minimum: 0,
                    StockRoomOnHand: 0,
                    ShelfOnHand: 0,
                    SessionID: id,
                    Remarks: "",
                    Requested: 0,
                    Executed: 0,
                    Lot: "",
                    Distributor: "",
                    LocationID: LocationMap.samples,
                    SyncStatus: SyncStatusMap.create
                };

                await addRecord('sessionItems', newItem);
            } catch (error) {
                console.error(`Failed to add item`);
            }
        }

        fetchData();
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);

            try {
                await fetchOnlineData();
                await fetchData();
            } catch (error) {
                console.error("Error during initial data fetch:", error);
            } finally {
                setIsLoading(false);
            }
        };
        reset();
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        const handleRefresh = async () => {
            await fetchOnlineData();
            await fetchData();
        };

        window.addEventListener("refreshSession", handleRefresh);

        return () => {
            window.removeEventListener("refreshSession", handleRefresh);
        };
    }, [id]);

    const fetchData = async () => {

        try {
            const invdata = await getAll('sessionItems');
            const sess = await getRecordById('session', id);
            const it = await getAll("item");
            const ut = await getAll("unit");

            const samples = it.filter((a) => a.Samples > 0);
            const sessionInv = invdata.filter((a) => a.SessionID === Number(id) && a.SyncStatus !== SyncStatusMap.done);


            setInvData(sessionInv);
            setSession(sess);
            setItem(it);
            setUnits(ut);

        } catch (error) {
            console.log(error);
        }
    };

    const processedData = useMemo(() => {
        const merged = invData.map(inv => {
            const itemDetails = item.find(i => i.ID === inv.ItemID) || {};
            const unitDetails = units.find(u => u.ID === inv.UnitID) || {};

            return {
                ...inv,
                Item: itemDetails,
                Unit: unitDetails
            };
        });

        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return merged;

        return merged.filter(inv => {
            const code = inv.Item?.Code?.toLowerCase() || '';
            const description = inv.Item?.Description?.toLowerCase() || '';
            const lot = inv.Lot?.toLowerCase() || '';

            return code.includes(normalizedSearch) ||
                description.includes(normalizedSearch) ||
                lot.includes(normalizedSearch);
        });
    }, [invData, item, units, search]);

    useEffect(() => {
        setData(processedData);
    }, [processedData]);

    const fetchOnlineData = async () => {
        try {
            setIsLoading(true);
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                const token = localStorage.getItem("token");
                const devId = localStorage.getItem("devId");

                const response = await fetch(`${appURL.API_CoverageInventory}/${id}/samples`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        "X-ETR-DeviceID": devId
                    },
                });

                if (!response.ok) {
                    setValidationMessage("Failed to load samples!");
                    setShowValidation(true);
                    return;
                }

                const data = await response.json();
                const transformData = await transformSessionInventorySamples(data?.items || [], id);
                await upsertRecordsBySessionId('sessionItems', id, transformData);
            }
        } catch (error) {
            setValidationMessage(error);
            setShowValidation(true);
        } finally {
            setIsLoading(false);
        }
    }

    const handleDelete = async () => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        // Delete all matching records in parallel
        const selectedIds = Object.keys(checkedRows).filter(key => checkedRows[key]);

        await Promise.all(selectedIds.map(async (ID) => {
            const sessionItem = await getRecordById('sessionItems', Number(ID));
            if (!sessionItem) return;

            const stockItem = await getSampleStockByItemId(sessionItem.ItemID, StockAllocationType.Samples);
            if (stockItem) {
                // Return executed quantity back to stock
                const updatedStock = {
                    ...stockItem,
                    Allocated: stockItem.Allocated - (sessionItem?.Executed || 0)
                };
                await updateRecord('StockAllocations', updatedStock);
            }

            await deleteRecord('sessionItems', Number(ID));
        }));

        fetchData();
        setCheckedRows({});
    };

    const handleInputChange = async (row, event, field) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        const value = event.target.value;

        if (/^\d*$/.test(value)) {
            const parsedQty = value === "" ? "" : Math.max(0, parseInt(value, 10));

            const sessionItem = await getRecordById('sessionItems', row.ID);
            const stockItem = await getSampleStockByItemId(sessionItem.ItemID, StockAllocationType.Samples);

            const allocated = stockItem.Allocated || 0;
            const available = (stockItem.Stock || 0) - allocated;

            const currentQty = sessionItem[field] || 0;
            const maxAllowed = currentQty + available;

            // Clamp the value to max allowed
            const finalQty = Math.min(parsedQty, maxAllowed);

            const stockChange = finalQty - currentQty;

            const updatedSessionItem = {
                ...sessionItem,
                [field]: parsedQty,
                SyncStatus: sessionItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
            }

            const newAllocated = Math.max(
                0,
                Math.min(stockItem.Stock, allocated + stockChange)
            );

            const updatedStockItem = {
                ...stockItem,
                Allocated: parsedQty
            };

            await updateRecord('sessionItems', updatedSessionItem);
            await updateRecord('StockAllocations', updatedStockItem);

            setData(prev =>
                prev.map(item =>
                    item.ID === row.ID ? { ...item, [field]: parsedQty, SyncStatus: updatedSessionItem.SyncStatus } : item
                )
            );
        }
    };

    const handleQuantityChange = async (row, change, field) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;
        const currentQty = row?.[field] || 0;

        const getSessionItem = await getRecordById('sessionItems', row.ID);
        const stockItem = await getSampleStockByItemId(row.ItemID, StockAllocationType.Samples);

        const allocated = stockItem.Allocated || 0;
        const available = (stockItem.Stock || 0) - allocated;

        // if (change > 0 && available === 0) return; // no stock to add
        if (change < 0 && currentQty === 0) return;     // nothing to subtract

        const newQuantity = currentQty + change;
        const stockChange = change;

        const updateItem = {
            ...getSessionItem,
            [field]: newQuantity,
            SyncStatus: getSessionItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
        }

        const newAllocated = Math.max(
            0,
            Math.min(stockItem.Stock, allocated + stockChange)
        );

        const updateStockItem = {
            ...stockItem,
            Allocated: newQuantity
        }

        await updateRecord('sessionItems', updateItem);
        await updateRecord('StockAllocations', updateStockItem);

        setData(prev =>
            prev.map(item =>
                item.ID === row.ID ? {
                    ...item,
                    [field]: newQuantity,
                    SyncStatus: updateItem.SyncStatus
                } : item
            )
        );
    };

    const handleHeaderCheckboxChange = () => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        const newCheckedState = !isAllChecked;
        setIsAllChecked(newCheckedState);

        const updatedCheckedRows = data.reduce((acc, item) => {
            acc[item.ItemID] = newCheckedState;
            return acc;
        }, {});

        setCheckedRows(updatedCheckedRows);
    };

    const handleCheckboxChange = (itemId) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        setCheckedRows((prev) => {
            const updated = { ...prev, [itemId]: !prev[itemId] };
            setIsAllChecked(Object.values(updated).every(Boolean)); // Update header checkbox state
            return updated;
        });
    };

    const handleFieldChange = async (row, field, value) => {
        if (![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync) return;

        const inventory = await getRecordById("sessionItems", row.ID)
        const updatedRow = {
            ...inventory,
            [field]: value,
            SyncStatus: inventory?.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
        };
        await updateRecord("sessionItems", updatedRow);
        setData(prevData =>
            prevData.map(item =>
                item.ID === updatedRow.ID ? { ...item, [field]: value, SyncStatus: updatedRow.SyncStatus } : item
            )
        );
    };

    // Helper function to group rows by item ID
    function groupByItemId(data) {
        return data.reduce((groups, row) => {
            const key = row.Item?.ID ?? 'unknown';
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(row);
            return groups;
        }, {});
    }

    const reset = () => {
        setIsAllChecked(false);
        setCheckedRows({});
        setSearch('');
        setValidationMessage('');
    };

    return (
        <div className="opal-samples mb-2 d-none d-md-block">
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header className="p-0 mb-2">
                        <div className="header-container d-flex justify-content-between w-100">
                            <div className="header-left">
                                <TauriImage localImage={section.icon} className="inv-icon-header me-2" />
                                <span className="header-title">{section?.header.toUpperCase()}</span>

                                <Button
                                    className="btn d-flex align-items-center justify-content-center p-0 ms-4"
                                    size="sm"
                                    disabled={![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync || loading}
                                    onClick={(e) => {
                                        e.stopPropagation(); // ✅ Prevent accordion toggle
                                        setShowItemModal(true); // ✅ Open modal
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="inv-icon-add me-2" />
                                    <div className="text-nowrap">Add Item</div>
                                </Button>
                            </div>
                            <div className="d-flex align-items-center justify-content-end header-filter">
                                {Object.values(checkedRows).some(checked => checked) && (
                                    <>
                                        <div className="d-flex justify-content-center col-samples">
                                            <input
                                                type="text"
                                                className="mx-1"
                                                value={batchValues.Executed ?? 0}
                                                onChange={(event) => handleBatchInputNumberChange(event, 'Executed')}
                                                onClick={(e) => { e.stopPropagation(); }} />
                                            <TauriImage
                                                localImage="/Images/garden--duplicate-stroke-16.svg"
                                                className="inv-icon-input ms-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBatchUpdate('Executed', batchValues.Executed)
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-center col-remarks">
                                            <input
                                                type="text"
                                                className="mx-1"
                                                value={batchValues.Remarks}
                                                onChange={e => handleBatchInputTextChange('Remarks', e.target.value)}
                                                onClick={(e) => { e.stopPropagation(); }} />
                                            <TauriImage
                                                localImage="/Images/garden--duplicate-stroke-16.svg"
                                                className="inv-icon-input ms-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBatchUpdate('Remarks', batchValues.Remarks)
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="col-find">
                                    <Button className="btn d-flex align-items-center justify-content-center p-0 m-0"
                                        size="sm"
                                        disabled={loading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowInventoryItemModal(true);
                                        }}>
                                        <TauriImage localImage="/Images/symbols--search.svg" className="inv-icon-find me-1" color={search ? "#FFBD59" : "#2292E3"} />
                                        <div>Find</div>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className="py-0 px-0">
                        {loading ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <div className="inv-table">
                                <table className="table">
                                    <thead className="inv-table-header">
                                        <tr>
                                            <th className="text-center col-delete">
                                                {Object.values(checkedRows).some(checked => checked) && (
                                                    <TauriImage
                                                        localImage="/Images/delete-icon.svg"
                                                        className="inv-icon-delete"
                                                        disabled={![statusMapping.pending, statusMapping.inprogress].includes(session?.Status) || isSync}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // ✅ Prevent accordion toggle
                                                            handleDelete(); // ✅ Open modal
                                                        }} />
                                                )}
                                            </th>
                                            <th className="col-item">
                                                <div>
                                                    <span>ITEM</span>
                                                    <span className="item-count">{data?.length} items</span>
                                                </div>
                                            </th>
                                            <th></th>
                                            <th className="text-center text-nowrap  col-samples">SAMPLES</th>
                                            <th className="text-center text-nowrap col-remarks">REMARKS</th>
                                        </tr>
                                    </thead>
                                    <tbody className="inv-table-body">
                                        {Object.entries(groupByItemId(data)).map(([itemId, rows]) => {
                                            return (
                                                <React.Fragment key={itemId}>
                                                    {rows.map((row, idx) => (
                                                        <tr key={row.ID}>
                                                            <td></td>
                                                            {idx === 0 && (
                                                                <td className="col-item align-top" rowSpan={rows.length}>
                                                                    <div className="d-flex flex-column">
                                                                        <div className="item-code">({row?.Item?.Code})</div>
                                                                        <div className="item-description">{row?.Item?.Description}</div>
                                                                    </div>
                                                                </td>
                                                            )}
                                                            <td className="col-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!checkedRows[row.ID]}
                                                                    onChange={() => handleCheckboxChange(row.ID)}
                                                                />
                                                            </td>
                                                            <td className="text-nowrap text-center col-samples">
                                                                <TauriImage
                                                                    localImage={"/Images/uil--plus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, 1, 'Executed')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="mx-1"
                                                                    value={row?.Executed ?? 0}
                                                                    onChange={(event) => handleInputChange(row, event, 'Executed')}
                                                                />
                                                                <TauriImage
                                                                    localImage={"/Images/uil--minus.svg"}
                                                                    alt="icon"
                                                                    onClick={() => handleQuantityChange(row, -1, 'Executed')}
                                                                    color={"#1D5D9B"}
                                                                />
                                                            </td>
                                                            <td className="text-value text-nowrap text-center col-remarks">
                                                                <input
                                                                    type="text"
                                                                    className="mx-1"
                                                                    maxLength={30}
                                                                    value={row.Remarks}
                                                                    onChange={e => handleFieldChange(row, 'Remarks', e.target.value)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <OpalAddItemModal
                show={showItemModal}
                onHide={() => setShowItemModal(false)}
                onAddSelected={handleAddSelected}
            />
            <OpalSamplesFindModal
                show={showInventoryItemModal}
                onHide={() => setShowInventoryItemModal(false)}
                search={search}
                setSearch={setSearch}
            />
            <OpalAlertModal
                show={showValidation}
                onHide={() => {
                    setShowValidation(false);
                }}
                title={"Message"}
                bodyText={validationMessage}
                modalType="message"
            />
        </div>
    );
};

export default OpalSamples;
